import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { RefreshCw, Search } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { z } from 'zod/v3';

import { JobStatus, loraApi } from '@/api/connectivity/lora';
import {
  BatchDeviceActivationLogTable,
  DeviceActivationLogTable,
} from '@/components/connectivity/activation-logs-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { TableSkeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Job status options for the select
const JOB_STATUS_OPTIONS = [
  { label: 'Pendente', value: JobStatus.JS_PENDING },
  { label: 'Processando', value: JobStatus.JS_PROCESSING },
  { label: 'Falhou', value: JobStatus.JS_FAILED },
  { label: 'Finalizado', value: JobStatus.JS_SUCCESS },
];

// Search form schema
const searchSchema = z.object({
  serial: z.string().optional(),
  status: z.string().optional(),
});

type SearchFormData = z.infer<typeof searchSchema>;

export function LoraActivationLogsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get params from URL
  const serial = searchParams.get('serial') || '';
  const status = searchParams.get('status') || '';
  const logPage = Number(searchParams.get('logPage')) || 1;
  const batchPage = Number(searchParams.get('batchPage')) || 1;
  const limit = 20;

  // Form setup
  const form = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      serial,
      status,
    },
  });

  // Query for single jobs
  const {
    data: jobs,
    isLoading: isLoadingJobs,
    isFetching: isFetchingJobs,
  } = useQuery({
    queryKey: ['jobs', logPage, serial, status],
    queryFn: () =>
      loraApi.findJobs({
        page: logPage,
        limit,
        serial: serial || undefined,
        status: (status as JobStatus) || undefined,
      }),
  });

  // Query for batch jobs
  const {
    data: batchJobs,
    isLoading: isLoadingBatchJobs,
    isFetching: isFetchingBatchJobs,
  } = useQuery({
    queryKey: ['batch-jobs', batchPage, serial, status],
    queryFn: () =>
      loraApi.findBatchJobs({
        page: batchPage,
        limit,
        serial: serial || undefined,
        status: (status as JobStatus) || undefined,
      }),
  });

  const isFiltering = isFetchingJobs || isFetchingBatchJobs;

  const handleSearch = form.handleSubmit((data) => {
    const params = new URLSearchParams(searchParams);
    params.set('logPage', '1');
    params.set('batchPage', '1');

    if (data.serial) {
      params.set('serial', data.serial);
    } else {
      params.delete('serial');
    }

    if (data.status) {
      params.set('status', data.status);
    } else {
      params.delete('status');
    }

    setSearchParams(params);
  });

  const handleClearFilters = () => {
    form.reset({ serial: '', status: '' });
    const params = new URLSearchParams();
    setSearchParams(params);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['batch-jobs'] });
  };

  const handleLogPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('logPage', String(page));
    setSearchParams(params);
  };

  const handleBatchPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('batchPage', String(page));
    setSearchParams(params);
  };

  const hasFilters = serial || status;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full space-y-6 overflow-y-auto p-6 py-10"
    >
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Logs de gerenciamento das chaves LoRa
          </h1>
          <p className="text-sm text-muted-foreground">
            Monitore o histórico e o gerenciamento das chaves LoRa registradas
          </p>
        </div>
      </header>

      <Separator />

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Input {...form.register('serial')} placeholder="Pesquisar por serial" className="w-64" />

          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <div className="w-64">
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <Button type="submit" disabled={isFiltering} size="icon" variant="outline">
            <Search />
          </Button>

          {hasFilters && (
            <Button
              type="button"
              disabled={isFiltering}
              variant="outline"
              onClick={handleClearFilters}
            >
              Limpar
            </Button>
          )}
        </div>

        <Button type="button" variant="outline" onClick={handleRefresh} disabled={isFiltering}>
          <RefreshCw size={14} />
          Atualizar
        </Button>
      </form>

      {/* Tabs with Tables */}
      <section>
        <Tabs defaultValue="single">
          <TabsList>
            <TabsTrigger value="single">Lista geral</TabsTrigger>
            <TabsTrigger value="batch">Em massa</TabsTrigger>
          </TabsList>

          <TabsContent value="single">
            {isLoadingJobs ? (
              <TableSkeleton rows={10} />
            ) : jobs ? (
              <DeviceActivationLogTable
                data={jobs}
                currentPage={logPage}
                onPageChange={handleLogPageChange}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="batch">
            {isLoadingBatchJobs ? (
              <TableSkeleton rows={10} />
            ) : batchJobs ? (
              <BatchDeviceActivationLogTable
                data={batchJobs}
                currentPage={batchPage}
                onPageChange={handleBatchPageChange}
              />
            ) : null}
          </TabsContent>
        </Tabs>
      </section>
    </motion.div>
  );
}
